import { useLocation, useNavigate, useParams } from "react-router-dom";
import Tooltip from '@mui/material/Tooltip';
import LiveSubRenderer from "./Components/LiveSubRenderer.tsx";
import { RootState, useAppSelector, useAppDispatch } from "../../../../state/state.tsx";
import { SubscriptionDocWithId } from "../../types/SubscriptionType.ts";
import { useDispatch } from "react-redux";
import { openModal } from "../../../../state/ModalSlice/ModalSlice.ts";
import {
    setFieldsForEditSubscriptionModal,
    setFieldsForQuickAddModal
} from "../../../../state/ArbitrarySlice/ArbitratySlice.ts";
import { LiveSubscriptionType } from "../../types/LiveSubscriptionTypes.ts";
import { useEffect, useState, useMemo } from "react";
import { OrderUi } from "../../types/OrderListUi.ts";
import SubsNotFound from "../../../../assets/GifsAndPlaceholders/SubsNotFound.png"
import { toast } from "react-toastify";
import AttendanceCalendar from "./Components/AttendanceCalendar.tsx";
import ProgressLogSection from "./Components/ProgressLogSection.tsx";
import GoogleMeetSection from "./Components/GoogleMeetSection.tsx";
import ZoomMeetingSection from "../../../../lib/zoom/components/ZoomMeetingSection.tsx";
import MeetPlusSection from "./Components/MeetPlusSection.tsx";
import { selectBankAccounts, selectEffectiveUID } from "../../../../state/UserStatesV2/userSliceV2.ts";
import { selectActiveRole } from "../../../../state/UserStatesV2/userSliceV2.ts";
import SyllabusSection from "./Components/SyllabusSection";
import NotesSection from "./Components/NotesSection";
import ChatSection from "./Components/ChatSection";
import { checkZoomAuth, shouldCheckZoomAuth } from "../../../../state/ZoomSlice/zoomSlice.ts";
import { checkGoogleMeetAuth, shouldCheckGoogleMeetAuth } from "../../../../state/GoogleMeetSlice/googleMeetSlice.ts";
import { isZoomEnabledForProvider } from "../../../../Config/firebase";
import { fetchSlotsFromFirestore } from '../../../../state/SlotsSlice/SlotsSlice';
import { Slot } from '../../types/SubscriptionType';
import LiveSubInformationSection from "./Components/LiveSubInformationSection.tsx";
import LiveSubOrdersRenderer from "./Components/LiveSubOrdersRenderer.tsx";
import { doc, setDoc, deleteDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { actions as actions_LiveSub } from "../../../../state/LiveSubscriptionSlice/LiveSubscriptionSlice.ts";
import { useBackend } from "../../../../hooks/backend/useBackend.ts";
import { GenericActions } from "../../../../state/GenericSlice.ts";
import { DocumentData } from 'firebase/firestore';

export const VITE_GOOGLE_MEET_OPEN = import.meta.env.VITE_GOOGLE_MEET_OPEN === 'TRUE';
export const VITE_SLOTS_ENABLED = import.meta.env.VITE_SLOTS_ENABLED === 'TRUE';
export const VITE_MEETPLUS_ENABLED = import.meta.env.VITE_MEETPLUS_ENABLED === 'TRUE';

interface SlotAssignment {
    studentId: string;
    studentName: string;
    assignedAt: any;
    status: string;
}

export default function SubscriptionDetailsPage() {

    const { Subscriptionid } = useParams<{ Subscriptionid: string }>();
    const Navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const liveSubscriptionId = queryParams.get('livesubid');
    useEffect(() => {
        if (liveSubscriptionId) {
            setSelectedLiveSubscriptionid(liveSubscriptionId);
        }
    }, [liveSubscriptionId]);
    // -------------- States

    const [selectedLiveSubscriptionid, setSelectedLiveSubscriptionid] = useState<string>("");
    const [searchQuery, setSearchQuery] = useState<string>("");

    // Add state for zoom enabled status
    const [zoomEnabled, setZoomEnabled] = useState<boolean>(false);

    // Add state for individual meeting types within Online Meetings
    const [showGoogleMeet, setShowGoogleMeet] = useState(VITE_GOOGLE_MEET_OPEN);
    const [showZoom, setShowZoom] = useState(zoomEnabled && !VITE_GOOGLE_MEET_OPEN);
    const [showMeetPlus, setShowMeetPlus] = useState(VITE_MEETPLUS_ENABLED); // Meet+ enabled only in dev or with env flag

    // Ensure at least one meeting type is selected - Meet+ is always first/default
    useEffect(() => {
        if (VITE_MEETPLUS_ENABLED) {
            // If Meet+ is enabled, make it default
            setShowMeetPlus(true);
            setShowGoogleMeet(false);
            setShowZoom(false);
        } else if (VITE_GOOGLE_MEET_OPEN) {
            // If Meet+ disabled but Google Meet enabled, default to Google Meet
            setShowMeetPlus(false);
            setShowGoogleMeet(true);
            setShowZoom(false);
        } else if (zoomEnabled) {
            // If only Zoom is enabled, default to Zoom
            setShowMeetPlus(false);
            setShowGoogleMeet(false);
            setShowZoom(true);
        }
    }, [zoomEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Add state for floating chat
    const [showFloatingChat, setShowFloatingChat] = useState(false);

    // Add state for student details tabs
    const [activeStudentTab, setActiveStudentTab] = useState<'payments' | 'attendance' | 'learning-status' | 'progress-log' | 'slots'>(() => {
        const savedStudentTab = localStorage.getItem('subscriptionDetailsStudentTab');
        return (savedStudentTab as 'payments' | 'attendance' | 'learning-status' | 'progress-log' | 'slots') || 'payments';
    });

    // Add state for expandable sections
    const [expandedSections, setExpandedSections] = useState({
        learning: true,
        slots: true,
        meetings: true
    });

    // Add state for active tab with localStorage persistence
    const [activeTab, setActiveTab] = useState<'learning' | 'slots' | 'meetings'>(() => {
        const savedTab = localStorage.getItem('subscriptionDetailsActiveTab');
        return (savedTab as 'learning' | 'slots' | 'meetings') || 'learning';
    });

    // Sub-tab for learning materials (syllabus and notes)
    const [learningSubTab, setLearningSubTab] = useState<'syllabus' | 'notes'>('syllabus');

    // Update localStorage when active student tab changes
    useEffect(() => {
        localStorage.setItem('subscriptionDetailsStudentTab', activeStudentTab);
    }, [activeStudentTab]);

    // Update localStorage when active tab changes
    useEffect(() => {
        localStorage.setItem('subscriptionDetailsActiveTab', activeTab);
    }, [activeTab]);

    // Toggle section expansion
    const toggleSection = (section: 'learning' | 'slots' | 'meetings') => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // ---------App Selectors
    const Dispatch = useDispatch();
    const appDispatch = useAppDispatch();

    const AllSubscription: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice.data) || []
    const subscriptionStatus = useAppSelector((state: RootState) => state.subscriptionTableSlice.status)

    const AllLiveSubscriptions: LiveSubscriptionType[] = useAppSelector((state: RootState) => state.LiveSubscriptionSlice.data) || []

    const AllOrders: OrderUi[] = useAppSelector((state: RootState) => state.OrderStatsSlice.data) || []

    const selectedSubscriptionArray: SubscriptionDocWithId[] = AllSubscription.filter((data) => data.id === Subscriptionid)

    const selectedSubscription: SubscriptionDocWithId = selectedSubscriptionArray[0]
    const providerId = useAppSelector(selectEffectiveUID);
    const activeRole = useAppSelector(selectActiveRole);
    
    // Add hook for refreshing live subscription data
    const { loadCollection: loadCollectionLiveSub } = useBackend(`ServiceProviders/${providerId}/LiveSubscriptions`);
    
    // Function to refresh live subscription data instead of full page reload
    const refreshLiveSubscriptionData = () => {
        if (providerId && loadCollectionLiveSub) {
            console.log('🔄 Refreshing live subscription data after phone number change');
            loadCollectionLiveSub(actions_LiveSub as GenericActions<DocumentData>, {
                sort: {
                    attribute: 'startDate',
                    order: 'desc'
                }
            }, activeRole);
        }
    };
    
    // Add zoom state selector
    const zoomState = useAppSelector((state: RootState) => state.zoomSlice.data) || {
        isAuthenticated: false,
        userEmail: null
    };
    const zoomStatus = useAppSelector((state: RootState) => state.zoomSlice.status);
    
    // Add Google Meet state selector
    const googleMeetState = useAppSelector((state: RootState) => state.googleMeetSlice.data) || {
        isAuthenticated: false,
        userEmail: null,
        userName: null
    };
    const googleMeetStatus = useAppSelector((state: RootState) => state.googleMeetSlice.status);
    
    // Debug logs for Zoom state changes
    useEffect(() => {
        console.log('🔍 SubscriptionDetails: Zoom state changed:', {
            isAuthenticated: zoomState.isAuthenticated,
            userEmail: zoomState.userEmail,
            status: zoomStatus
        });
    }, [zoomState.isAuthenticated, zoomState.userEmail, zoomStatus]);

    // Debug logs for Google Meet state changes
    useEffect(() => {
        console.log('🔍 SubscriptionDetails: Google Meet state changed:', {
            isAuthenticated: googleMeetState.isAuthenticated,
            userEmail: googleMeetState.userEmail,
            userName: googleMeetState.userName,
            status: googleMeetStatus
        });
    }, [googleMeetState.isAuthenticated, googleMeetState.userEmail, googleMeetState.userName, googleMeetStatus]);

    // Check zoom enabled status
    useEffect(() => {
        if (providerId) {
            const enabled = isZoomEnabledForProvider();
            setZoomEnabled(enabled);
        }
    }, [providerId]);

    // Check Zoom authentication status when component mounts
    useEffect(() => {
        if (providerId && zoomEnabled) {
            // Only check if we don't have recent persisted state
            if (shouldCheckZoomAuth()) {
                console.log('Checking Zoom auth for provider:', providerId);
                appDispatch(checkZoomAuth(providerId));
            } else {
                console.log('Using persisted Zoom auth state for provider:', providerId);
            }
        }
    }, [providerId, zoomEnabled, appDispatch]);

    // Check Google Meet authentication status when component mounts
    useEffect(() => {
        if (providerId && VITE_GOOGLE_MEET_OPEN) {
            // Only check if we don't have recent persisted state
            if (shouldCheckGoogleMeetAuth()) {
                console.log('Checking Google Meet auth for provider:', providerId);
                appDispatch(checkGoogleMeetAuth(providerId));
            } else {
                console.log('Using persisted Google Meet auth state for provider:', providerId);
            }
        }
    }, [providerId, appDispatch]);

    // Add periodic Zoom auth check to catch newly connected accounts - but less aggressive
    useEffect(() => {
        if (!providerId || !zoomEnabled) return;

        const checkZoomAuthPeriodically = async () => {
            try {
                // Only check if we actually need to
                if (shouldCheckZoomAuth()) {
                    console.log('Periodic Zoom auth check for provider:', providerId);
                    await appDispatch(checkZoomAuth(providerId));
                } else {
                    console.log('Skipping periodic Zoom auth check - using persisted state');
                }
            } catch (error) {
                console.error('Error in periodic Zoom auth check:', error);
            }
        };

        // Check every 30 seconds for the first 2 minutes to catch newly connected accounts
        const intervals: NodeJS.Timeout[] = [];
        for (let i = 1; i <= 4; i++) {
            const interval = setTimeout(checkZoomAuthPeriodically, i * 30000); // 30, 60, 90, 120 seconds
            intervals.push(interval);
        }

        // Then check every 5 minutes after that (instead of 2 minutes)
        const longInterval = setInterval(checkZoomAuthPeriodically, 5 * 60 * 1000);

        return () => {
            intervals.forEach(interval => clearTimeout(interval));
            clearInterval(longInterval);
        };
    }, [providerId, zoomEnabled, appDispatch]);

    // Add periodic Google Meet auth check to catch newly connected accounts
    useEffect(() => {
        if (!providerId || !VITE_GOOGLE_MEET_OPEN) return;

        const checkGoogleMeetAuthPeriodically = async () => {
            try {
                // Only check if we actually need to
                if (shouldCheckGoogleMeetAuth()) {
                    console.log('Periodic Google Meet auth check for provider:', providerId);
                    await appDispatch(checkGoogleMeetAuth(providerId));
                } else {
                    console.log('Skipping periodic Google Meet auth check - using persisted state');
                }
            } catch (error) {
                console.error('Error in periodic Google Meet auth check:', error);
            }
        };

        // Check every 30 seconds for the first 2 minutes to catch newly connected accounts
        const intervals: NodeJS.Timeout[] = [];
        for (let i = 1; i <= 4; i++) {
            const interval = setTimeout(checkGoogleMeetAuthPeriodically, i * 30000); // 30, 60, 90, 120 seconds
            intervals.push(interval);
        }

        // Then check every 5 minutes after that
        const longInterval = setInterval(checkGoogleMeetAuthPeriodically, 5 * 60 * 1000);

        return () => {
            intervals.forEach(interval => clearTimeout(interval));
            clearInterval(longInterval);
        };
    }, [providerId, appDispatch]);

    // Fetch slots for this subscription
    const [slots, setSlots] = useState<Slot[]>([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    useEffect(() => {

        const loadSlots = async () => {
            if (providerId && selectedSubscription?.id) {
                setSlotsLoading(true);
                try {
                    const result = await fetchSlotsFromFirestore(providerId, selectedSubscription.id);
                    setSlots(result.slots);
                } catch (error) {
                    console.error('Error loading slots:', error);
                } finally {
                    setSlotsLoading(false);
                }
            }
        };
        loadSlots();
    }, [providerId, selectedSubscription?.id]);


    let filteredLiveSubscriptions: LiveSubscriptionType[] = []
    let filteredOrders: OrderUi[] = []
    let selectedLiveSubscription: LiveSubscriptionType | null = null
    type Binary = 0 | 1;
    let days: Binary[] = [1, 1, 1, 1, 1, 1, 1];
    let selectedLiveSubscriptionArray = []

    if (selectedSubscription) {
        filteredLiveSubscriptions = AllLiveSubscriptions
            .filter((data) => data.subscriptionPlanRefId === selectedSubscription.id)
            .sort((a, b) => a.studentName.localeCompare(b.studentName));

        filteredOrders = AllOrders.filter((data) => data.livesubscriptionId === selectedLiveSubscriptionid)
        selectedLiveSubscriptionArray = filteredLiveSubscriptions.filter((data) => data.id === selectedLiveSubscriptionid)
        selectedLiveSubscription = selectedLiveSubscriptionArray[0]
        days = selectedSubscription.days;
    }

    filteredLiveSubscriptions = useMemo(() => {
        return filteredLiveSubscriptions
            .filter(data =>
                data.studentName.toLowerCase().includes(searchQuery.toLowerCase())
            );
    }, [filteredLiveSubscriptions, searchQuery]);

    // -------------- onChange Handlers

    const onChangeSelectedLiveSubscriptionid = (id: string) => {
        setSelectedLiveSubscriptionid(id)
    }


    // ---------OnClick Handlers
    const bankAccount = useAppSelector(selectBankAccounts);

    const onClickAddSubscriber = (id: string, subcriptionName: string, type: string, advance: number, Price: number, align2monthlyBoundary: boolean) => {
        if (bankAccount.length === 0) {
            toast.error("Please add bank account, before adding student")
            Dispatch(openModal("AddBankAccountOpen"))
            return
        }

        Dispatch(setFieldsForQuickAddModal({
            id: id, subscriptionName: subcriptionName, type: type, advance: advance, Price: Price, align2monthlyBoundary: align2monthlyBoundary
        }))
        Dispatch(openModal("AddLiveSubscriptionModalOpen"))

    }


    const onClickEditSubscription = (id: string, showMeInList: boolean, Name: string, Price: string, paymentProfileId: number) => {
        Dispatch(setFieldsForEditSubscriptionModal({
            id: id,
            showMeInList: showMeInList,
            Name: Name,
            Price: Price,
            paymentProfileID: paymentProfileId
        }))
        Dispatch(openModal("EditSubscriptionModalOpen"))
    }

    // Update state for slot assignments
    const [slotAssignments, setSlotAssignments] = useState<{ [key: string]: SlotAssignment[] }>({});
    const [loadingAssignments, setLoadingAssignments] = useState(true);
    const [processingSlotId, setProcessingSlotId] = useState<string | null>(null);

    // Fetch assignments directly from Firestore
    useEffect(() => {
        const fetchAssignments = async () => {
            if (!providerId || !selectedSubscription?.id || !slots.length) {
                setLoadingAssignments(false);
                setSlotAssignments({});
                return;
            }
            
            try {
                setLoadingAssignments(true);
                const newAssignments: { [key: string]: SlotAssignment[] } = {};
                
                // Fetch assignments for each slot
                for (const slot of slots) {
                    const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscription.id}/Slots/${slot.id}/Assignments`);
                    const assignmentsSnap = await getDocs(assignmentsRef);
                    
                    newAssignments[slot.id] = assignmentsSnap.docs.map(doc => ({
                        studentId: doc.id,
                        ...doc.data() as Omit<SlotAssignment, 'studentId'>
                    }));
                }
                
                setSlotAssignments(newAssignments);
            } catch (error) {
                console.error('Error fetching slot assignments:', error);
                toast.error('Failed to load slot assignments');
            } finally {
                setLoadingAssignments(false);
            }
        };

        fetchAssignments();
    }, [providerId, selectedSubscription?.id, slots]);

    // Add function to check if a student is assigned to a slot
    const isStudentAssignedToSlot = (slotId: string) => {
        return slotAssignments[slotId]?.some(assignment => assignment.studentId === selectedLiveSubscription?.id);
    };

    // Add function to get current assignments count for a slot
    const getSlotAssignmentCount = (slotId: string) => {
        return slotAssignments[slotId]?.length || 0;
    };

    const handleAssignSlot = async (slotId: string) => {
        if (!selectedLiveSubscription || !providerId || !selectedSubscription?.id) {
            toast.error('Please select a student first');
            return;
        }

        setProcessingSlotId(slotId);

        try {
            const slot = slots.find(s => s.id === slotId);
            if (!slot) {
                toast.error('Slot not found');
                return;
            }

            // Check if student is already in this slot
            const isAssigned = isStudentAssignedToSlot(slotId);

            if (isAssigned) {
                // Remove student from slot
                try {
                    // Find the document that contains this studentId
                    const assignmentsRef = collection(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscription.id}/Slots/${slotId}/Assignments`);
                    const assignmentsSnap = await getDocs(assignmentsRef);
                    const targetDoc = assignmentsSnap.docs.find(doc => doc.data().studentId === selectedLiveSubscription.id);
                    
                    if (targetDoc) {
                        await deleteDoc(targetDoc.ref);
                        
                        // Update local state
                        setSlotAssignments(prev => ({
                            ...prev,
                            [slotId]: prev[slotId]?.filter(a => a.studentId !== selectedLiveSubscription.id) || []
                        }));
                        
                        toast.success('Student removed from slot');
                    } else {
                        console.error('No document found with studentId:', selectedLiveSubscription.id);
                        toast.error('Assignment not found in database');
                    }
                } catch (error) {
                    console.error('Error removing student from slot:', error);
                    toast.error('Failed to remove student from slot');
                }
            } else {
                // Add student to slot
                // Check capacity before adding
                if (getSlotAssignmentCount(slotId) >= slot.maxCapacity) {
                    toast.error('Slot is at maximum capacity');
                    return;
                }

                try {
                    const assignmentRef = doc(db, `ServiceProviders/${providerId}/Subscriptions/${selectedSubscription.id}/Slots/${slotId}/Assignments/${selectedLiveSubscription.id}`);
                    await setDoc(assignmentRef, {
                        studentId: selectedLiveSubscription.id,
                        studentName: selectedLiveSubscription.studentName,
                        assignedAt: serverTimestamp(),
                        status: 'active'
                    });
                    
                    // Update local state
                    setSlotAssignments(prev => ({
                        ...prev,
                        [slotId]: [...(prev[slotId] || []), {
                            studentId: selectedLiveSubscription.id,
                            studentName: selectedLiveSubscription.studentName,
                            assignedAt: new Date(),
                            status: 'active'
                        }]
                    }));
                    
                    toast.success('Student assigned to slot');
                } catch (error) {
                    console.error('Error assigning student to slot:', error);
                    toast.error('Failed to assign student to slot');
                }
            }
        } catch (error) {
            console.error('Error managing slot assignment:', error);
            toast.error('Failed to manage slot assignment');
        } finally {
            setProcessingSlotId(null);
        }
    };

    if (subscriptionStatus === 'loading' || subscriptionStatus === undefined) {
        return (
            <div className={"flex justify-center items-center h-screen"}>
                <div className={"flex flex-col items-center gap-4"}>
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cc_brand"></div>
                    <p className={"text-lg text-gray-600"}>Loading subscription details...</p>
                </div>
            </div>
        );
    }

    if (!selectedSubscription) {
        return <div className={"flex justify-center items-center"}>
            <div className={" flex flex-col justify-center items-center"}><img className={"h-[65vh] w-auto"}
                src={SubsNotFound} alt={"no Sub found"} />
                <p className={"font-medium my-4 "}>Subscription is not Found!</p>
                <button className={"bg-cc_brand p-3 rounded-lg text-white font-medium"} onClick={() => {
                    Navigate("/mysubscriptions")
                }}>Go to Home
                </button>
            </div>

        </div>
    }

    return (<div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-screen">
        {/* Column 1: Subscription Details + Expandable Sections + Students List */}
        <div className={`transition-all duration-300 ${showFloatingChat ? 'lg:col-span-4' : 'lg:col-span-6'}`}>
            <div className="border-[1px] border-t-cc_brand border-t-4 border-gray-300 h-full bg-white shadow-xl rounded-xl"
                style={{ boxShadow: "0 0px 6px 0px rgba(0, 0, 0, 0.1), 0 0px 6px 0px rgba(0, 0, 0, 0.06)" }}>
                
                {/* Subscription Details Header */}
                <div className="subscription-details px-5 pt-2.5 pb-2.5 rounded-xl">
                    
                    <div className={"flex justify-between mt-2 mb-1"}>
                        <p className={"text-cc_brand text-cf_subHeading font-semibold "}>{selectedSubscription.Name}</p>
                        <p className={"text- font-medium"}> {selectedSubscription.subscriptionType === "Monthly" ? `Monthly ₹${activeRole?.role === "Observer" ? "XXX" : selectedSubscription.Price}` :
                            selectedSubscription.subscriptionType === "Count" ? (selectedSubscription.isCountHourly ?
                                <span className="">Count Hourly</span> :
                                "Count Based Pricing") :
                                `Multi-Month Prices`}</p>
                    </div>
                    <div className={"flex justify-between"}>
                        <span className={"flex items-center gap-1 max-w-[40%]"}>

                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="feather feather-map-pin"><path
                                    d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10"
                                        r="3"></circle></svg>
                            <p className={"max-w-[80%] overflow-ellipsis text-nowrap text-sm overflow-hidden"}>{selectedSubscription.placeName}</p>
                        </span>

                        {activeRole?.role !== "Observer" && <div className={"flex gap-2"}>
                            {VITE_MEETPLUS_ENABLED && <Tooltip title={"Launch Chequered Chess"}>
                                <button
                                    onClick={() => {
                                        Navigate(`/chequered/${selectedSubscription.id}`);
                                    }}
                                    className={"p-2 hover:bg-purple-600 hover:text-white rounded-full cursor-pointer text-purple-600 transition-colors border-2 border-purple-600"}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z"></path>
                                        <rect x="2" y="14" width="20" height="8" rx="1"></rect>
                                    </svg>
                                </button>
                            </Tooltip>}
                            <Tooltip title={"View All Orders"}>
                                <button
                                    onClick={() => {
                                        Navigate({
                                            pathname: '/orders',
                                            search: `?subscriptionId=${selectedSubscription.id}`
                                        });
                                    }}
                                    className={"p-2 hover:bg-cc_brand hover:text-white rounded-full cursor-pointer text-cc_brand transition-colors"}
                                >
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                                        <line x1="1" y1="10" x2="23" y2="10"></line>
                                    </svg>
                                </button>
                            </Tooltip>
                            <Tooltip title={"Edit Subscription"}>
                                <div onClick={() => {
                                    onClickEditSubscription(selectedSubscription.id, selectedSubscription.showMeInList, selectedSubscription.Name, selectedSubscription.Price.toString(), selectedSubscription.paymentProfileId)
                                }} className={"p-2 hover:bg-cc_brand hover:text-white rounded-full cursor-pointer text-cc_brand transition-colors"}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                        strokeLinejoin="round" className="feather feather-edit">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                </div>
                            </Tooltip>
                        </div>}
                    </div>
                </div>

                {/* Tabbed Sections with Expandable Details */}
                <div className="px-5 pt-2.5 pb-2.5 border-t border-gray-200">
                    <div className="border-[1px] border-gray-300 rounded-lg bg-white overflow-hidden">
                        {/* Tab Headers */}
                        <div className="flex border-b border-gray-200 bg-gray-50">
                            <button
                                onClick={() => {
                                    setActiveTab('learning');
                                    setExpandedSections(prev => ({ ...prev, learning: true }));
                                }}
                                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                    activeTab === 'learning' 
                                        ? 'bg-white text-cc_brand border-b-2 border-cc_brand' 
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                                    </svg>
                                    Learning Materials
                                </div>
                            </button>
                            {VITE_SLOTS_ENABLED && (
                                <button
                                    onClick={() => {
                                        setActiveTab('slots');
                                        setExpandedSections(prev => ({ ...prev, slots: true }));
                                    }}
                                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                        activeTab === 'slots' 
                                            ? 'bg-white text-cc_brand border-b-2 border-cc_brand' 
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        Slots
                                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full border border-orange-200">
                                            Preview
                                        </span>
                                    </div>
                                </button>
                            )}
                            {(VITE_GOOGLE_MEET_OPEN || zoomEnabled || VITE_MEETPLUS_ENABLED) && (
                                <button
                                    onClick={() => {
                                        setActiveTab('meetings');
                                        setExpandedSections(prev => ({ ...prev, meetings: true }));
                                    }}
                                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                        activeTab === 'meetings' 
                                            ? 'bg-white text-cc_brand border-b-2 border-cc_brand' 
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                        </svg>
                                        Live Classes
                                    </div>
                                </button>
                            )}
                        </div>

                        {/* Tab Content with Expandable Details */}
                        <div className="p-4">
                            {/* Expandable Details Toggle */}
                            <button
                                onClick={() => toggleSection(activeTab)}
                                className="w-full mb-4 px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between border border-gray-200 rounded-lg"
                            >
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">
                                        {activeTab === 'learning' && 'Learning Materials Details'}
                                        {activeTab === 'slots' && 'Slots Details'}
                                        {activeTab === 'meetings' && 'Meeting Details'}
                                    </span>
                                    {/* Syllabus Status Indicator */}
                                    {activeTab === 'learning' && (
                                        selectedSubscription.assignedSyllabus && 
                                        Array.isArray(selectedSubscription.assignedSyllabus) && 
                                        selectedSubscription.assignedSyllabus.length > 0 ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                Associated Syllabus Found
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">
                                                No Syllabus Associated
                                            </span>
                                        )
                                    )}
                                </div>
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    className={`transition-transform duration-200 ${expandedSections[activeTab] ? 'rotate-180' : ''}`}
                                >
                                    <polyline points="6,9 12,15 18,9"></polyline>
                                </svg>
                            </button>

                            {/* Expandable Content */}
                            <div className={`transition-all duration-300 ease-in-out overflow-hidden ${expandedSections[activeTab] ? 'max-h-[32rem]' : 'max-h-0'}`}>
                                <div className="overflow-y-auto max-h-[30rem]">
                                    {/* Learning Materials Tab (Syllabus + Notes) */}
                                    {activeTab === 'learning' && (
                                        <div className="space-y-4">
                                            {/* Sub-tabs for Syllabus and Notes */}
                                            <div className="flex gap-2 border-b border-gray-200 pb-2">
                                                <button
                                                    onClick={() => setLearningSubTab('syllabus')}
                                                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                                                        learningSubTab === 'syllabus'
                                                            ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                                                            : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    📚 Syllabus
                                                </button>
                                                <button
                                                    onClick={() => setLearningSubTab('notes')}
                                                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                                                        learningSubTab === 'notes'
                                                            ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-600'
                                                            : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    📝 Class Notes
                                                </button>
                                            </div>

                                            {/* Syllabus Content */}
                                            {learningSubTab === 'syllabus' && (
                                                <div className="space-y-6">
                                                    <SyllabusSection
                                                        subscription={selectedSubscription}
                                                        providerId={providerId}
                                                        importForAll={true}
                                                    />
                                                </div>
                                            )}

                                            {/* Notes Content */}
                                            {learningSubTab === 'notes' && (
                                                <div className="space-y-6">
                                                    <NotesSection
                                                        subscriptionId={selectedSubscription.id}
                                                        providerId={providerId}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Slots Tab */}
                                    {VITE_SLOTS_ENABLED && activeTab === 'slots' && (
                                        <div>
                                            {slotsLoading ? (
                                                <div className="text-center text-gray-500">Loading slots...</div>
                                            ) : slots.length === 0 ? (
                                                <div className="text-center text-gray-500">
                                                    <p className="mb-3">No slots created yet.</p>
                                                    {activeRole?.role !== "Observer" && (
                                                        <button
                                                            onClick={() => {
                                                                Navigate(`/slots/${selectedSubscription.id}`);
                                                            }}
                                                            className="px-4 py-2 bg-cc_brand text-white rounded hover:bg-blue-700 transition-colors"
                                                        >
                                                            Add First Slot
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {slots.map((slot: Slot) => (
                                                        <div key={slot.id} className="flex flex-col md:flex-row md:items-center md:justify-between border border-gray-200 rounded-lg p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                                                            <div className="flex flex-col md:flex-row md:items-center gap-2">
                                                                <span className="font-medium">{slot.dayOfWeek.join(', ')}</span>
                                                                <span className="text-gray-600">{slot.startTime} ({slot.durationMinutes} min)</span>
                                                                <span className="text-gray-600">Capacity: {slot.maxCapacity}</span>
                                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${slot.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{slot.isActive ? 'Active' : 'Inactive'}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {slots.length > 0 && (
                                                        <div className="mt-4 flex justify-center">
                                                            <button
                                                                onClick={() => {
                                                                    Navigate(`/slots/${selectedSubscription.id}`);
                                                                }}
                                                                className="px-4 py-2 bg-cc_brand text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                                                </svg>
                                                                View All Slots
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Online Meetings Tab */}
                                    {activeTab === 'meetings' && (
                                        <div className="space-y-4">
                                            {/* Meeting Type Tabs */}
                                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                                <div className="flex border-b border-gray-200">
                                                    {/* Meet+ Tab - Only in development */}
                                                    {VITE_MEETPLUS_ENABLED && (
                                                        <button
                                                            onClick={() => {
                                                                setShowMeetPlus(true);
                                                                setShowGoogleMeet(false);
                                                                setShowZoom(false);
                                                            }}
                                                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                                                showMeetPlus 
                                                                    ? 'bg-gradient-to-r from-purple-500 to-blue-600 text-white' 
                                                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-center gap-2">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M23 7l-7 5 7 5V7z"></path>
                                                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                                                </svg>
                                                                Meet+ by Hosaksham
                                                            </div>
                                                        </button>
                                                    )}
                                                    
                                                    {VITE_GOOGLE_MEET_OPEN && (
                                                        <button
                                                            onClick={() => {
                                                                setShowGoogleMeet(true);
                                                                setShowZoom(false);
                                                                setShowMeetPlus(false);
                                                            }}
                                                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                                                showGoogleMeet 
                                                                    ? 'bg-cc_brand text-white' 
                                                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-center gap-2">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M23 7l-7 5 7 5V7z"></path>
                                                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                                                                </svg>
                                                                Google Meet
                                                            </div>
                                                        </button>
                                                    )}
                                                    {zoomEnabled && (
                                                        <button
                                                            onClick={() => {
                                                                setShowZoom(true);
                                                                setShowGoogleMeet(false);
                                                                setShowMeetPlus(false);
                                                            }}
                                                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                                                                showZoom 
                                                                    ? 'bg-cc_brand text-white' 
                                                                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-center gap-2">
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                                </svg>
                                                                Zoom Meetings
                                                            </div>
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Meeting Content - Fixed Height with Scroll */}
                                                <div className="p-4 h-96 overflow-y-auto">
                                                    {/* Meet+ Content - Only in development */}
                                                    {VITE_MEETPLUS_ENABLED && showMeetPlus && (
                                                        <div className="text-sm">
                                                            <MeetPlusSection
                                                                subscriptionId={selectedSubscription.id}
                                                                subscriptionName={selectedSubscription.Name}
                                                                userRole={activeRole?.role}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Google Meet Content */}
                                                    {VITE_GOOGLE_MEET_OPEN && showGoogleMeet && (
                                                        <div className="text-sm">
                                                            <GoogleMeetSection
                                                                serviceProviderId={providerId}
                                                                subscriptionId={selectedSubscription.id}
                                                                googleMeetings={(selectedSubscription as any).googleMeetings || []}
                                                                subscriptionName={selectedSubscription.Name}
                                                            />
                                                        </div>
                                                    )}

                                                    {/* Zoom Meetings Content */}
                                                    {zoomEnabled && showZoom && (
                                                        <div className="text-sm">
                                                            {zoomState.isAuthenticated ? (
                                                                <ZoomMeetingSection
                                                                    serviceProviderId={providerId}
                                                                    subscriptionId={selectedSubscription.id}
                                                                    subscriptionName={selectedSubscription.Name}
                                                                    userRole={activeRole?.role}
                                                                />
                                                            ) : (
                                                                <div className="text-center py-8">
                                                                    <div className="mb-4">
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-400">
                                                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                                        </svg>
                                                                    </div>
                                                                    <h4 className="text-md font-medium text-gray-900 mb-2">Connect Zoom Account</h4>
                                                                    <p className="text-sm text-gray-600 mb-4">
                                                                        Connect your Zoom account to create and manage meetings.
                                                                    </p>
                                                                    <button
                                                                        onClick={() => {
                                                                            Navigate('/profile');
                                                                            const url = new URL(window.location.href);
                                                                            url.searchParams.set('openMeetingIntegrations', 'true');
                                                                            window.history.replaceState({}, '', url.toString());
                                                                        }}
                                                                        className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                                                                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                                        </svg>
                                                                        Connect Zoom Account
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Students List Section - More Space */}
                <div className="p-5 border-t border-gray-200 flex-1 ">
                    <div className="border-[1px] border-gray-300 rounded-lg bg-white max-h-[80vh] overflow-y-auto h-full flex flex-col">
                        <div className="p-4 border-b border-gray-200 flex-shrink-0">
                            <div className={"flex justify-between"}>
                                <p className={"text-[18px] font-semibold"}>All Students</p>
                                {activeRole?.role !== "Observer" && <div className="flex gap-2 items-center">
                                    <Tooltip title={"Add Student"}>
                                        <button
                                            onClick={() => {
                                                onClickAddSubscriber(
                                                    selectedSubscription.id,
                                                    selectedSubscription.Name,
                                                    selectedSubscription.type,
                                                    selectedSubscription.advance,
                                                    selectedSubscription.Price,
                                                    selectedSubscription.align2monthlyBoundary
                                                )
                                            }}
                                            className={"flex justify-center items-center bg-cc_brand rounded-md p-1"}
                                        >
                                            <svg width="18" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9.5 3V15M15.5 9L3.5 9" stroke="#F9FAFB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </Tooltip>
                                </div>}
                            </div>

                            <div className="relative mt-3 mb-2">
                                <input
                                    type="text"
                                    placeholder="Search subscribers..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full px-4 py-2 rounded-lg border border-cc_modal-grey-border focus:outline-none focus:border-cc_brand focus:ring-1 focus:ring-cc_brand"
                                />
                                <svg
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                            </div>
                        </div>


                        <div className="p-4 overflow-y-auto flex-1">

                            {filteredLiveSubscriptions.length > 0 ? (
                                filteredLiveSubscriptions
                                    .map((data: LiveSubscriptionType) => {
                                        return (
                                            <LiveSubRenderer
                                                key={data.id}
                                                liveSubDoc={data}
                                                onChangeSelectedLiveSubscriptionid={onChangeSelectedLiveSubscriptionid}
                                                selectedSubscriptionPrice={selectedSubscription.Price}
                                            />
                                        )
                                    })
                            ) : (
                                <div className={"flex flex-col items-center p-4 justify-center"}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                        strokeLinejoin="round" className="feather feather-info">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <line x1="12" y1="16" x2="12" y2="12"></line>
                                        <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                    </svg>
                                    <p>No Students Found!</p>
                                    <p>Please add a new Student.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        {/* Column 2: Student Details with Tabs */}
        <div className={`transition-all duration-300 ${showFloatingChat ? 'lg:col-span-5' : 'lg:col-span-6'}`}>
            {selectedLiveSubscription ? (
                <div className="border-[1px] border-gray-300 h-full bg-white shadow-xl rounded-xl overflow-hidden">
                    {/* Student Info Section - Always Visible */}
                    <div className="p-4 border-b border-gray-200">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Student Information</h4>
                        {activeRole?.role !== "Observer" ? (
                            <LiveSubInformationSection 
                                selectedLiveSubDoc={selectedLiveSubscription} 
                                selectedSubscriptionPrice={selectedSubscription.Price} 
                                isAlign2monthBoundary={selectedSubscription.align2monthlyBoundary} 
                                NoGSTBatch={selectedSubscription.NoGSTBatch}
                                serviceProviderId={providerId}
                                onDataRefresh={refreshLiveSubscriptionData}
                            />
                        ) : (
                            <div className="text-center text-gray-500">
                                <p className="text-lg font-medium text-gray-900 mb-3">Student Name: {selectedLiveSubscription.studentName}</p>
                                <p className="text-sm font-medium text-gray-900 mb-3">ID: {selectedLiveSubscription.id}</p>
                                <p>Student information is not available for observers.</p>
                            </div>
                        )}
                    </div>
                    
                    {/* Student Details Tabs */}
                    <div className="border-b border-gray-200">
                        <div className="flex">
                            {activeRole?.role !== "Observer" && (
                                <button
                                    onClick={() => setActiveStudentTab('payments')}
                                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                                        activeStudentTab === 'payments' 
                                            ? 'bg-cc_brand text-white' 
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    Payments
                                </button>
                            )}
                            {selectedSubscription.type !== "Rentals" && (
                                <button
                                    onClick={() => setActiveStudentTab('attendance')}
                                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                                        activeStudentTab === 'attendance' 
                                            ? 'bg-cc_brand text-white' 
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    Attendance
                                </button>
                            )}
                            <button
                                onClick={() => setActiveStudentTab('learning-status')}
                                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                                    activeStudentTab === 'learning-status' 
                                        ? 'bg-cc_brand text-white' 
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                Learning Status
                            </button>
                            <button
                                onClick={() => setActiveStudentTab('progress-log')}
                                className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                                    activeStudentTab === 'progress-log' 
                                        ? 'bg-cc_brand text-white' 
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                Progress Log
                            </button>
                            {import.meta.env.VITE_SLOTS_ENABLED === 'TRUE' && (
                                <button
                                    onClick={() => setActiveStudentTab('slots')}
                                    className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                                        activeStudentTab === 'slots' 
                                            ? 'bg-cc_brand text-white' 
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className="flex items-center justify-center gap-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        Slots
                                    </div>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Student Details Content */}
                    <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
                        {/* Payments Tab */}
                        {activeStudentTab === 'payments' && (
                            <div>
                                {filteredOrders.length > 0 ? (
                                    filteredOrders.map((data, index) => (
                                        <LiveSubOrdersRenderer OrderData={data} key={index} />
                                    ))
                                ) : (
                                    <div className="flex flex-col items-center p-4 justify-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                            strokeLinejoin="round" className="feather feather-info">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                        </svg>
                                        <p>Please Initiate Payment</p>
                                        <p>No Orders Found!</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Attendance Tab */}
                        {activeStudentTab === 'attendance' && (
                            <div className="h-full">
                                <AttendanceCalendar
                                    userId={selectedLiveSubscription.id}
                                    livesubId={selectedLiveSubscription.id}
                                    subscriptionId={Subscriptionid || ''}
                                    userName={selectedLiveSubscription.studentName}
                                    subscriptionDays={days.map(day => Boolean(day))}
                                    isCountHourly={Boolean(selectedSubscription.isCountHourly)}
                                    slots={slots}
                                    slotAssignments={slotAssignments}
                                />
                            </div>
                        )}

                        {/* Learning Status Tab */}
                        {activeStudentTab === 'learning-status' && (
                            <div className="space-y-4">
                                <div>
                                    <SyllabusSection
                                        subscription={selectedSubscription}
                                        providerId={providerId}
                                        liveSubId={selectedLiveSubscription.id}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Progress Log Tab */}
                        {activeStudentTab === 'progress-log' && (
                            <div className="space-y-4">
                                <div>
                                    <ProgressLogSection
                                        liveSubId={selectedLiveSubscription.id}
                                        studentName={selectedLiveSubscription.studentName}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Slots Tab */}
                        {activeStudentTab === 'slots' && (
                            <div className="space-y-4">
                                {loadingAssignments ? (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cc_brand"></div>
                                    </div>
                                ) : slots.length === 0 ? (
                                    <div className="text-center py-8">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-gray-400 mb-4">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Slots Available</h3>
                                        <p className="text-gray-600 mb-4">
                                            No slots have been created for this subscription yet.
                                        </p>
                                        {activeRole?.role !== "Observer" && (
                                            <button
                                                onClick={() => Navigate(`/slots/${selectedSubscription.id}`)}
                                                className="px-4 py-2 bg-cc_brand text-white rounded-lg hover:bg-blue-600 transition-colors"
                                            >
                                                Create Slots
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg font-medium text-gray-900">Available Slots</h3>
                                            {activeRole?.role !== "Observer" && (
                                                <button
                                                    onClick={() => Navigate(`/slots/${selectedSubscription.id}`)}
                                                    className="px-3 py-1.5 text-sm bg-cc_brand text-white rounded-lg hover:bg-blue-600 transition-colors"
                                                >
                                                    Manage Slots
                                                </button>
                                            )}
                                        </div>
                                        
                                        {slots.map((slot: Slot) => (
                                            <div key={slot.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className="font-medium text-gray-900">
                                                                {slot.dayOfWeek.join(', ')}
                                                            </span>
                                                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                                slot.isActive 
                                                                    ? 'bg-green-100 text-green-800' 
                                                                    : 'bg-red-100 text-red-800'
                                                            }`}>
                                                                {slot.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </div>
                                                        <div className="text-sm text-gray-600">
                                                            <span>{slot.startTime} ({slot.durationMinutes} min)</span>
                                                            <span className="mx-2">•</span>
                                                            <span>Capacity: {getSlotAssignmentCount(slot.id)}/{slot.maxCapacity}</span>
                                                        </div>
                                                    </div>
                                                    {activeRole?.role !== "Observer" && slot.isActive && (
                                                        <button
                                                            onClick={() => handleAssignSlot(slot.id)}
                                                            disabled={!selectedLiveSubscription || 
                                                                (!isStudentAssignedToSlot(slot.id) && getSlotAssignmentCount(slot.id) >= slot.maxCapacity) ||
                                                                processingSlotId !== null}
                                                            className={`ml-4 px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                                                                !selectedLiveSubscription
                                                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                    : processingSlotId === slot.id
                                                                        ? 'bg-blue-100 text-blue-700 cursor-not-allowed'
                                                                        : isStudentAssignedToSlot(slot.id)
                                                                            ? 'bg-red-600 text-white hover:bg-red-700'
                                                                            : getSlotAssignmentCount(slot.id) >= slot.maxCapacity
                                                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                                : 'bg-green-600 text-white hover:bg-green-700'
                                                            }`}
                                                        >
                                                            {processingSlotId === slot.id ? (
                                                                <>
                                                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                                                    <span>{isStudentAssignedToSlot(slot.id) ? 'Removing...' : 'Assigning...'}</span>
                                                                </>
                                                            ) : (
                                                                isStudentAssignedToSlot(slot.id) ? 'Remove' : 'Assign'
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="border-[1px] border-gray-300 h-full bg-white shadow-xl rounded-xl flex items-center justify-center">
                    <div className="text-center text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-gray-300">
                            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="m22 21-2-2"></path>
                            <path d="M16 16l4 4"></path>
                        </svg>
                        <p className="text-lg font-medium">Select a student</p>
                        <p className="text-sm">Choose a student from the list to view details</p>
                    </div>
                </div>
            )}
        </div>

        {/* Column 3: Collapsible Group Chat Column */}
        <div className={`transition-all duration-300 ${showFloatingChat ? 'lg:col-span-3' : 'lg:col-span-0'}`}>
            {showFloatingChat && (
                <ChatSection
                    subscriptionId={selectedSubscription.id}
                    subscriptionName={selectedSubscription.Name}
                    isDetachable={false}
                    context="subscription"
                    onClose={() => setShowFloatingChat(false)}
                />
            )}
        </div>

        {/* Chat Toggle Button - Only show when chat is collapsed */}
        {false && !showFloatingChat && (
            <div className="fixed bottom-4 right-4 z-50">
                <button
                    onClick={() => setShowFloatingChat(true)}
                    className="bg-cc_brand text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
                    title="Open chat"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                </button>
            </div>
        )}
    </div>)
}